import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { SourcingInputAppBasketService } from './sourcing-input-app-basket.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import * as _ from 'lodash-es';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-sourcing-input-app-basket',
    templateUrl: './sourcing-input-app-basket.component.html',
    styleUrls: ['./sourcing-input-app-basket.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SourcingInputAppBasketComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly contextSubs = new Subscription();
  private contextEpoch = 0;

  // 🚀 CRITICAL: Filter params input for dynamic data
  filterParams: any = {};

  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};

  rowData: any = {};
  private rowDataFromHost = false;
  private filterParamsFromHost = false;

  // 🚀 CRITICAL: colData input to differentiate modal type
  @Input('col-data')
  set _col_data(colData: string){
    try{
      if (typeof colData === 'string' && (colData.includes('{{') || colData.trim() === '')) {
        return;
      }
      let data: any = safeJsonInput<any>(colData, null, 'sourcing-input-app-basket.parse1');
      if(Object.keys(data).length > 0){
        this.colData = data;
        this.determineModalType();
        this.scheduleTryInit();
      }
    }catch(e){
      console.log("Col data input not yet stable. Still waiting to get resolved.");
    }
  }
  colData: any = {};

  @Input('scenario')
  set _scenario(scenario: string){
    try{
      if (typeof scenario === 'string' && (scenario.includes('{{') || scenario.trim() === '')) {
        return;
      }
      this.scenario = safeJsonInput<any>(scenario, null, 'sourcing-input-app-basket.parse2');
      this.enrichScenarioFromContext();
      this.scheduleTryInit();
    }catch(e){
      console.log("Scenario input not yet stable. Still waiting to get resolved.");
    }
  }

  @Input('row-data')
  set _row_data(val: string) {
    try {
      if (typeof val === 'string' && (val.includes('{{') || val.trim() === '')) {
        return;
      }
      const parsed = typeof val === 'string' ? safeJsonInput<any>(val, null, 'sourcing-input-app-basket.parse3') : val;
      if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
        this.rowData = extractObject(parsed);
        this.normalizeRowDataTpKey();
        this.rowDataFromHost = true;
        this.scheduleTryInit();
      }
    } catch (_) {
      console.log('Row data input not yet stable. Still waiting to get resolved.');
    }
  }

  @Input('base-urls')
  set _base_urls(val: string) {
    try {
      if (typeof val === 'string' && (val.includes('{{') || val.trim() === '')) {
        return;
      }
      const parsed = typeof val === 'string' ? safeJsonInput<any>(val, null, 'sourcing-input-app-basket.parse4') : val;
      if (parsed && typeof parsed === 'object') {
        this.baseURLs = { ...this.baseURLs, ...parsed };
        if (parsed.api) {
          this.GENERAL_CONFIG = { ...this.GENERAL_CONFIG, base_url: parsed.api, api: parsed.api };
        }
        this.scheduleTryInit();
      }
    } catch (_) {
      // Host binding not yet stable
    }
  }

  @Input('filter-params')
  set _filter_params(val: string) {
    try {
      if (typeof val === 'string' && (val.includes('{{') || val.trim() === '')) {
        return;
      }
      const parsed = typeof val === 'string' ? safeJsonInput<any>(val, null, 'sourcing-input-app-basket.parse5') : val;
      if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
        this.filterParams = extractObject(parsed);
        this.filterParamsFromHost = true;
        this.scheduleTryInit();
      }
    } catch (_) {
      console.log('Filter params input not yet stable. Still waiting to get resolved.');
    }
  }

  // Component properties
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  
  private isInitialized = false;
  /** Invalidates in-flight HTTP after cancel, destroy, or a newer load. */
  private loadSeq = 0;
  /** Coalesces @Input setters + ngOnInit into a single init pass. */
  private initScheduled = false;

  // Standard component properties
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;
  loading = true;

  // Modal differentiation - based on colData.call_back
  isAppModal: boolean = false; // true for Apportionment Base modal, false for Basket modal
  modalTitle: string = 'Select Apportionment Base';
  
  // Getter for modalType to match template usage
  get modalType(): string {
    return this.isAppModal ? 'app' : 'basket';
  }

  // Getter for table colspan - dynamic based on Issue ID column visibility
  get tableColspan(): number {
    return this.issueIdEnabled() ? 3 : 2;
  }

  // App Modal Properties
  appBases: any[] = [];
  selectedAppBase: any = null;
  selectedIssueId: any = null;
  appIssueId: any[] = [];
  appBaseIssueKey: any[] = [];
  BASE_LEVEL: string = '';
  isbaseLevelDisabled: string = '';
  gridType: string = 'HO'; // 'HO' or 'QBU'
  scenario: any = {};

  // Basket Modal Properties
  baskets: any[] = [];
  basketTable: any[] = [];
  objects: any[] = []; // New baskets to add (legacy - kept for compatibility)
  newBasketTables: any[] = []; // New baskets as separate tables
  issueDropdown: any[] = [];
  issueDropdown_edit: any[] = [];
  default_basket: any = null;
  unsourced_amt_modal: number = 0;
  tableMinimiseAdd: boolean = false;
  tableMinimiseNew: boolean = false;
  showNewBasketTable: boolean = false;
  disableTable: boolean = false;
  basketRowFlag: boolean = false;
  issueIdRowFlag: boolean = false;
  issueRowFlag: boolean = false;
  hasServiceError: boolean = false;
  
  // Original values for reset (stored when data is first loaded)
  private originalBasketTable: any[] = [];
  private originalUnsourcedAmt: number = 0;
  private originalDefaultBasket: any = null;
  // Original values for App Modal reset
  private originalSelectedAppBase: any = null;
  private originalSelectedIssueId: any = null;
  private originalBaseLevel: string = '';

  // Labels
  labelSaveAndUpdate: string = "Save and Update";
  labelReset: string = "Reset";
  labelCancel: string = "Cancel";
  saveClickedMsg: string = "Please wait while we save this request";
  saveError: string = "Save Failed.";
  saveSuccess: string = "Data Saved Successfully.";
  sameAppBaseError: string = "Selected Apportionment Base is same as the existing Apportionment Base. Please select a different one.";
  sameAppBaseError_QBU: string = "Selected Apportionment Base and Base level is same as the existing Apportionment Base and Base level. Please select a different one.";
  totalRowReadonlyWarning: string = "This cell is not editable.";
  tableTitle: string = "Edit Baskets";
  app_issue_flag: boolean = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private sourcingInputAppBasketService: SourcingInputAppBasketService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.scheduleTryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        if (!this.isInitialized) {
          this.applyWorkspaceContext();
          this.scheduleTryInit();
        }
      })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        if (!this.isInitialized) {
          this.applyWorkspaceContext();
          this.scheduleTryInit();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.contextEpoch++;
    this.loadSeq++;
    this.contextSubs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    if (!this.filterParamsFromHost || !Object.keys(this.filterParams).length) {
      this.filterParams = fp;
    }
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    // Services read flat tax_year / scenario on globalService (legacy header.globalParams).
    this.globalService = { ...extractObject(mergedGp) };
    this.USER_SETTINGS = extractObject(shell.userSettingsData);
    this.SERVER_CONFIG = extractObject(shell.serverConfigData);
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc as GtwElementsBaseUrlsModel;
    if (!this.rowDataFromHost || !Object.keys(this.rowData).length) {
      const rawRow = rowPayloadFromActiveScreen(activeScreen);
      this.rowData = Array.isArray(rawRow)
        ? extractObject(rawRow[0]?.data ?? rawRow[0])
        : extractObject(rawRow);
    }
    this.normalizeRowDataTpKey();
  }

  /** Align TP_KEY with grid payloads that expose tp_key (sourcing-project-tagging pattern). */
  private normalizeRowDataTpKey(): void {
    if (!this.rowData || typeof this.rowData !== 'object') {
      return;
    }
    if (this.rowData.TP_KEY == null && this.rowData.tp_key != null && this.rowData.tp_key !== '') {
      this.rowData.TP_KEY = this.rowData.tp_key;
    }
  }

  /**
   * Resolve issue-id flag from scenario object, filter params, or global params.
   * Legacy filter uses is_issue_key_enabled; scenario dropdown uses ISSUE_ID_ENABLED.
   * Default N when unknown — undefined must not show Issue ID (original bug).
   */
  private resolveIssueIdEnabledFlag(): string {
    const candidates = [
      this.scenario?.ISSUE_ID_ENABLED,
      this.scenario?.is_issue_key_enabled,
      this.filterParams?.ISSUE_ID_ENABLED,
      this.filterParams?.is_issue_key_enabled,
      this.globalService?.ISSUE_ID_ENABLED,
      this.globalService?.is_issue_key_enabled
    ];
    for (const value of candidates) {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return String(value).trim().toUpperCase();
      }
    }
    return 'N';
  }

  issueIdEnabled(): boolean {
    return this.resolveIssueIdEnabledFlag() !== 'N';
  }

  private enrichScenarioFromContext(): void {
    if (!this.scenario || typeof this.scenario !== 'object') {
      this.scenario = {};
    }
    const flag = this.resolveIssueIdEnabledFlag();
    this.scenario.ISSUE_ID_ENABLED = flag;
    if (this.scenario.CODE == null && this.filterParams?.scenarioCode) {
      this.scenario.CODE = this.filterParams.scenarioCode;
    }
  }

  private finishAppModalLoad(seq: number): void {
    if (seq !== this.loadSeq) {
      return;
    }
    this.loading = false;
    this.showFormLoader = false;
    this.cdr.detectChanges();
  }

  private applyExistingBasketsResponse(response: any, storeOriginal = true): void {
    if (response.callSuccess === '1' && response.jsonObject) {
      response.jsonObject.forEach((val: any) => {
        val.ISSUE_KEY = val.ISSUE_KEY.toString().trim();
      });
      this.basketTable = response.jsonObject;
      this.default_basket = this.basketTable.find((x: any) => x.REC_TYPE === 'D');
      if (storeOriginal) {
        this.originalBasketTable = safeJsonInput<any>(JSON.stringify(response.jsonObject), null, 'sourcing-input-app-basket.parse6');
        this.originalUnsourcedAmt = this.unsourced_amt_modal;
        this.originalDefaultBasket = this.default_basket
          ? safeJsonInput<any>(JSON.stringify(this.default_basket), null, 'sourcing-input-app-basket.parse7')
          : null;
      }
      this.disableTable = false;
    } else {
      this.basketTable = [];
      this.default_basket = null;
      this.disableTable = false;
    }
  }

  private refreshBasketData(): void {
    const seq = this.loadSeq;
    if (!this.rowData || Object.keys(this.rowData).length === 0 || !this.hasApiBaseUrl()) {
      return;
    }
    this.unsourced_amt_modal = this.rowData.UNSOURCED_APPORTIONED_AMT ?? this.unsourced_amt_modal;

    this.sourcingInputAppBasketService.getExistingBaskets(
      this.apiBaseUrl(),
      this.filterParams,
      this.globalService,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (seq !== this.loadSeq) {
          return;
        }
        this.applyExistingBasketsResponse(response, true);
        this.objects = [];
        this.showNewBasketTable = false;
        this.basketRowFlag = false;
        this.issueIdRowFlag = false;
        this.issueRowFlag = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        if (seq !== this.loadSeq) {
          return;
        }
        console.error('Error refreshing existing baskets:', error);
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Defer init until all @Input bindings and workspace context are stable (one pass per modal open).
   */
  private scheduleTryInit(): void {
    if (this.isInitialized || this.initScheduled) {
      return;
    }
    this.initScheduled = true;
    Promise.resolve().then(() => {
      this.initScheduled = false;
      if (this.isInitialized) {
        return;
      }
      this.applyWorkspaceContext();
      this.enrichScenarioFromContext();
      this.tryInit();
    });
  }

  /** HTTP base URL only; does not alter payloads passed to services. */
  private apiBaseUrl(): string {
    return (this.GENERAL_CONFIG && this.GENERAL_CONFIG.base_url) || this.baseURLs?.api || '';
  }

  private hasApiBaseUrl(): boolean {
    const base = this.apiBaseUrl();
    return !!base && base.trim().length > 0;
  }

  private tryInit(): void {
    if (this.isInitialized) {
      return;
    }

    if (!this.rowData || Object.keys(this.rowData).length === 0 ||
        !this.scenario || !this.colData || Object.keys(this.colData).length === 0 ||
        !this.hasApiBaseUrl()) {
      return;
    }

    this.isInitialized = true;
    this.loading = true;
    this.showFormLoader = true;
    this.init();
  }

  init(): void {
    const seq = ++this.loadSeq;
    try {
      // Determine grid type
      if (this.resolveIssueIdEnabledFlag() === 'Y' && (this.globalService.issue_id == '')) {
        this.hasServiceError = true;
      }
      this.gridType = (this.rowData.GRID_TYPE) ? 'QBU' : 'HO';
      this.unsourced_amt_modal = this.rowData.UNSOURCED_APPORTIONED_AMT || 0;
      
      // 🚀 CRITICAL: Store original unsourced amount for reset
      this.originalUnsourcedAmt = this.unsourced_amt_modal;
      
      // Load data based on modal type
      if (this.isAppModal) {
        this.loadAppBases(seq);
      } else {
        this.loadBaskets(seq);
      }
    } catch (e) {
      console.error('Error in component init:', e);
      this.showFormLoader = false;
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Determine modal type based on colData.call_back
   * Following AngularJS pattern: if colData.call_back === "sourcngInputScreenModal" → App modal, else Basket modal
   */
  private determineModalType(): void {
    if (this.colData && this.colData.call_back === "sourcngInputScreenModal") {
      this.isAppModal = true;
      this.modalTitle = "Select Apportionment Base";
    } else {
      this.isAppModal = false;
      this.modalTitle = "Add/Edit Basket";
    }
  }

  /**
   * Load Apportionment Bases (App Modal)
   * Following AngularJS loadAppBases() function
   */
  loadAppBases(seq: number): void {
    // Check if total row (readonly)
    if (this.rowData['GGROWCOLORTOTAL'] === 1) {
      this.gtwSnackbarService.openSnackBar(this.totalRowReadonlyWarning, 'gtw-snackbar--yellow');
      this.cancel();
      return;
    }

    this.loading = true;
    this.showFormLoader = true;

    // Set BASE_LEVEL from rowData
    this.BASE_LEVEL = this.rowData.QBU_AMT_IND || '';
    // 🚀 CRITICAL: Store original BASE_LEVEL for reset
    this.originalBaseLevel = this.BASE_LEVEL;
    
    // Check if base level should be disabled
    if (['3041', '3112', '3116'].indexOf(this.rowData.GHOST_ACCT) > 0) {
      this.isbaseLevelDisabled = "disabled";
    }

    const issueEnabled = this.issueIdEnabled();

    this.sourcingInputAppBasketService.getAppBases(
      this.apiBaseUrl(),
      this.filterParams,
      this.globalService,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (seq !== this.loadSeq) {
          return;
        }
        if (response.callSuccess === '1' && response.jsonObject) {
          this.appBases = response.jsonObject;
          for (let i = 0; i < this.appBases.length; i++) {
            if (this.appBases[i].BASE_ID && this.rowData.BASE_ID &&
                this.appBases[i].BASE_ID === this.rowData.BASE_ID) {
              this.selectedAppBase = this.appBases[i];
              this.originalSelectedAppBase = safeJsonInput<any>(JSON.stringify(this.appBases[i]), null, 'sourcing-input-app-basket.parse8');
              break;
            }
          }
        } else {
          this.gtwSnackbarService.openSnackBar(response.errorMessage || 'Error loading apportionment bases', 'gtw-snackbar--red');
        }
        if (!issueEnabled) {
          this.finishAppModalLoad(seq);
        }
      },
      (error: any) => {
        if (seq !== this.loadSeq) {
          return;
        }
        console.error('Error loading app bases:', error);
        this.finishAppModalLoad(seq);
      }
    );

    if (!issueEnabled) {
      return;
    }

    this.sourcingInputAppBasketService.getAppBaseIssueKey(
      this.apiBaseUrl(),
      this.filterParams,
      this.globalService,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (seq !== this.loadSeq) {
          return;
        }
        if (response.callSuccess === '1' && response.jsonObject) {
          this.appBaseIssueKey = response.jsonObject;

          this.sourcingInputAppBasketService.getIssueId(
            this.apiBaseUrl(),
            this.filterParams,
            this.globalService,
            this.USER_SETTINGS
          ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
            (issueResponse: any) => {
              if (seq !== this.loadSeq) {
                return;
              }
              if (issueResponse.callSuccess === '1' && issueResponse.jsonObject) {
                this.appIssueId = issueResponse.jsonObject;
                this.appIssueId.forEach((issue: any) => {
                  if (this.appBaseIssueKey.length > 0 &&
                      this.appBaseIssueKey[0].ISSUE_KEY == issue.ISSUE_ID) {
                    this.selectedIssueId = issue;
                    this.originalSelectedIssueId = safeJsonInput<any>(JSON.stringify(issue), null, 'sourcing-input-app-basket.parse9');
                  }
                });
              } else {
                this.gtwSnackbarService.openSnackBar(issueResponse.errorMessage || 'Error loading issue IDs', 'gtw-snackbar--red');
              }
              this.finishAppModalLoad(seq);
            },
            (error: any) => {
              if (seq !== this.loadSeq) {
                return;
              }
              console.error('Error loading issue IDs:', error);
              this.gtwSnackbarService.openSnackBar('Error loading issue IDs. Please try again.', 'gtw-snackbar--red');
              this.finishAppModalLoad(seq);
            }
          );
        } else {
          this.finishAppModalLoad(seq);
        }
      },
      (error: any) => {
        if (seq !== this.loadSeq) {
          return;
        }
        console.error('Error loading app base issue key:', error);
        this.gtwSnackbarService.openSnackBar('Error loading apportionment data. Please try again.', 'gtw-snackbar--red');
        this.finishAppModalLoad(seq);
      }
    );
  }

  /**
   * Load Baskets (Basket Modal)
   * Following AngularJS loadBaskets() function
   */
  loadBaskets(seq: number): void {
    // Check if total row (readonly)
    if (this.rowData['GGROWCOLORTOTAL'] === 1) {
      this.gtwSnackbarService.openSnackBar(this.totalRowReadonlyWarning, 'gtw-snackbar--yellow');
      this.cancel();
      return;
    }

    this.loading = true;
    this.showFormLoader = true;
    this.disableTable = false;

    this.sourcingInputAppBasketService.getBaskets(
      this.apiBaseUrl(),
      this.filterParams,
      this.globalService,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (seq !== this.loadSeq) {
          return;
        }
        if (response.callSuccess === '1' && response.jsonObject) {
          this.baskets = response.jsonObject;
        } else {
          this.gtwSnackbarService.openSnackBar(response.errorMessage || 'Error loading baskets', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        if (seq !== this.loadSeq) {
          return;
        }
        console.error('Error loading baskets:', error);
        this.cdr.detectChanges();
      }
    );

    if (this.issueIdEnabled()) {
      this.sourcingInputAppBasketService.getIssueId(
        this.apiBaseUrl(),
        this.filterParams,
        this.globalService,
        this.USER_SETTINGS
      ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (response: any) => {
          if (seq !== this.loadSeq) {
            return;
          }
          if (response.callSuccess === '1' && response.jsonObject) {
            this.issueDropdown = response.jsonObject;
            this.issueDropdown_edit = response.jsonObject;
          } else {
            this.gtwSnackbarService.openSnackBar(response.errorMessage || 'Error loading issue IDs', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        (error: any) => {
          if (seq !== this.loadSeq) {
            return;
          }
          console.error('Error loading issue IDs:', error);
          this.gtwSnackbarService.openSnackBar('Error loading issue IDs. Please try again.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      );
    }

    this.sourcingInputAppBasketService.getExistingBaskets(
      this.apiBaseUrl(),
      this.filterParams,
      this.globalService,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (seq !== this.loadSeq) {
          return;
        }
        this.applyExistingBasketsResponse(response, true);
        this.loading = false;
        this.showFormLoader = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        if (seq !== this.loadSeq) {
          return;
        }
        console.error('Error loading existing baskets:', error);
        this.disableTable = true;
        this.basketTable = [];
        this.loading = false;
        this.showFormLoader = false;
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Save Apportionment Base (App Modal)
   * Following AngularJS saveApportionmentBase() function
   */
  saveApportionmentBase(): void {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar(this.saveClickedMsg, 'gtw-snackbar--red');
      return;
    }

    // Validate: Check if same apportionment base selected
    if (this.selectedAppBase && this.selectedAppBase.BASE_ID && this.rowData.BASE_ID &&
        this.selectedAppBase.BASE_ID === this.rowData.BASE_ID) {
      if (this.rowData.GRID_TYPE && this.rowData.GRID_TYPE === 'QBU') {
        if (this.rowData.QBU_AMT_IND === this.BASE_LEVEL) {
          this.gtwSnackbarService.openSnackBar(this.sameAppBaseError_QBU, 'gtw-snackbar--red');
          return;
        }
      } else {
        this.gtwSnackbarService.openSnackBar(this.sameAppBaseError, 'gtw-snackbar--red');
        return;
      }
    }

    // Get issue ID
    let app_issue_id = '0';
    if (this.selectedIssueId && this.selectedIssueId.ISSUE_ID) {
      app_issue_id = this.selectedIssueId.ISSUE_ID.toString();
    }

    // Validate issue ID if required
    if (this.issueIdEnabled() && app_issue_id === '0') {
      this.gtwSnackbarService.openSnackBar('Please select issue Id', 'gtw-snackbar--red');
      return;
    }

    this.crudLoading = true;
    this.isSaveClicked = true;
    this.cdr.detectChanges();

    const saveSeq = this.loadSeq;

    // Build save data
    const jsonSettings = _.clone(this.filterParams);
    jsonSettings.jcd_key = this.filterParams?.jcd_key || this.globalService?.jcd_key || '250';
    
    const jsonObj = _.clone(this.rowData);
    jsonObj.PREV_SOURCE_ID = this.rowData.BASE_ID;
    jsonObj.SOURCE_TYPE = 'A';
    jsonObj.SOURCE_ID = this.selectedAppBase.BASE_ID;
    jsonObj.ISSUE_KEY = app_issue_id;
    jsonObj.FILING_GROUP = this.rowData.FILING_GROUP;
    jsonObj.BASE_LEVEL = this.BASE_LEVEL;

    this.sourcingInputAppBasketService.saveApportionmentBase(
      this.apiBaseUrl(),
      this.USER_SETTINGS?.user?.sso_id || '',
      jsonSettings,
      jsonObj,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (saveSeq !== this.loadSeq) {
          return;
        }
        this.crudLoading = false;
        this.isSaveClicked = false;
        
        if (response.callSuccess === "1") {
          this.gtwSnackbarService.openSnackBar(this.saveSuccess, 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar(response.errorMessage || this.saveError, 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        if (saveSeq !== this.loadSeq) {
          return;
        }
        this.crudLoading = false;
        this.isSaveClicked = false;
        this.gtwSnackbarService.openSnackBar(this.saveError, 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Add New Basket Row
   * Following AngularJS addNew() function
   * Adds new row to single table
   */
  addNew(): void {
    const row: any = {
      id: this.objects.length + 1,
      combination_key: this.rowData.COMBINATION_KEY,
      basketDesc: "",
      baskets: [],
      basketAmount: 0,
      issueDropdown: [],
      issueId: "",
      flag: "add_basket",
      basketFlag: false,
      issueIdFlag: false
    };
    
    this.objects.push(row);
    this.showNewBasketTable = true;
    this.cdr.detectChanges();
  }

  /**
   * Change Basket Amount
   * Following AngularJS change_amount() function
   */
  change_amount(issue_id_enabled: string, index?: number): void {
    if (issue_id_enabled === 'N') {
      this.default_basket = this.basketTable.find((x: any) => x.REC_TYPE === 'D');
      let edit_total: number = 0;
      let total: number = 0;

      // Calculate edit_total from existing baskets
      if (this.basketTable !== undefined) {
        const edit_basket = _.filter(this.basketTable, (o: any) => o.REC_TYPE === 'M');
        if (edit_basket.length > 0) {
          for (let i = 0; i < edit_basket.length; i++) {
            edit_total = edit_total + parseInt(edit_basket[i].AMOUNT || 0);
          }
        } else {
          edit_total = 0;
        }
      } else {
        edit_total = 0;
      }

      // Calculate total from new baskets
      if (this.objects !== undefined) {
        for (let k = 0; k < this.objects.length; k++) {
          total = total + parseInt(this.objects[k].basketAmount || 0);
        }
      } else {
        total = 0;
      }

      // Update default basket amount
      if (this.default_basket !== undefined) {
        this.default_basket.AMOUNT = this.rowData.ADJUSTED_BOOK - (edit_total + total);
      } else {
        this.unsourced_amt_modal = this.rowData.ADJUSTED_BOOK - (edit_total + total);
      }
    }
    this.cdr.detectChanges();
  }

  /**
   * Handle amount input - parse value and update model.
   * Legacy used ng-model directly, which natively kept a leading "-" in the input
   * until the user typed the first digit. With one-way [value]+numberWithCommas
   * we have to avoid re-rendering the input while the value is still "-" / "-." /
   * "." otherwise the pipe would format null as "" and erase the user's keystroke.
   */
  onAmountInput(event: any, record: any, field: string = 'AMOUNT', index?: number): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Remove commas and any non-numeric characters except minus and decimal point
    const cleanValue = value.replace(/[^0-9.-]/g, '');

    // Partial/intermediate states the user can legitimately type on the way to a real number
    const isPartial = cleanValue === '-' || cleanValue === '.' || cleanValue === '-.';

    if (isPartial) {
      // Keep model as null but DO NOT re-render the input - leave the raw character in place
      record[field] = null;
      // Skip the auto-recalc (no numeric value yet) and skip detectChanges so the input
      // is not reformatted by numberWithCommas('').
      return;
    }

    const numValue = cleanValue === '' ? null : parseFloat(cleanValue);
    record[field] = (numValue !== null && !isNaN(numValue)) ? numValue : null;

    // Trigger change detection so pipe formats the display
    this.cdr.detectChanges();

    // Call change_amount to update totals (pass index if provided)
    if (index !== undefined) {
      this.change_amount(this.resolveIssueIdEnabledFlag(), index);
    } else {
      this.change_amount(this.resolveIssueIdEnabledFlag());
    }
  }

  /**
   * Handle amount blur - ensure model is updated
   */
  onAmountBlur(event: any, record: any, field: string = 'AMOUNT'): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    if (value) {
      const cleanValue = value.replace(/,/g, '');
      const numValue = cleanValue === '' ? null : parseFloat(cleanValue);
      record[field] = (numValue !== null && !isNaN(numValue)) ? numValue : null;
      this.cdr.detectChanges();
    }
  }

  /**
   * Validate New Baskets
   * Following AngularJS validateNewBaskets() function
   */
  validateNewBaskets(): void {
    for (let i = 0; i < this.objects.length; i++) {
      if ((this.objects[i].basketDesc === '' || this.objects[i].basketDesc === null)) {
        this.objects[i].basketFlag = true;
      }
    }
  }

  /**
   * Validate Issue ID
   * Following AngularJS validateIssueId() function
   */
  validateIssueId(): void {
    for (let i = 0; i < this.objects.length; i++) {
      if (this.objects[i].issueId === "") {
        this.objects[i].issueIdFlag = true;
      }
    }
  }

  /**
   * Validate Issue Flag for existing baskets
   * Following AngularJS validateIssueFlag() function
   */
  validateIssueFlag(): void {
    for (let i = 0; i < this.basketTable.length; i++) {
      if (this.basketTable[i].ISSUE_KEY === "") {
        this.basketTable[i].issueFlag = true;
      }
    }
  }

  /**
   * Single Row Save (Basket Modal)
   * Following AngularJS singleRowSave() function
   */
  singleRowSave(): void {
    let json_arrOBJ: any[] = [];
    let total_basket: number = 0;

    // Validate new baskets
    if (this.objects !== undefined) {
      this.validateNewBaskets();
      
      for (let i = 0; i < this.objects.length; i++) {
        if (this.objects[i].basketFlag === true) {
          this.basketRowFlag = true;
          break;
        } else {
          this.basketRowFlag = false;
        }
      }

      // Validate issue IDs if required
      if (this.issueIdEnabled()) {
        this.validateIssueId();
        this.validateIssueFlag();

        for (let i = 0; i < this.objects.length; i++) {
          if (this.objects[i].issueIdFlag === true) {
            this.issueIdRowFlag = true;
            break;
          } else {
            this.issueIdRowFlag = false;
          }
        }

        for (let i = 0; i < this.basketTable.length; i++) {
          if (this.basketTable[i].issueFlag === true) {
            this.issueRowFlag = true;
            break;
          } else {
            this.issueRowFlag = false;
          }
        }
      }

      // Build json array for new baskets
      _.each(this.objects, (item: any) => {
        json_arrOBJ.push({
          basket_amount: Number(item.basketAmount || 0),
          basket_id: item.basketDesc.BASKET_ID,
          basket_name: item.basketDesc.BASKET_NAME,
          issue_key: item.issueId && item.issueId.ISSUE_ID ? item.issueId.ISSUE_ID : "",
          rec_type: 'M',
          row_id: '0'
        });
      });
    }

    // Build json array for existing baskets
    if (this.basketTable !== undefined) {
      _.each(this.basketTable, (item: any) => {
        json_arrOBJ.push({
          basket_amount: item.AMOUNT,
          basket_id: item.BASKET_ID,
          basket_name: item.BASKET_NAME,
          issue_key: item.ISSUE_KEY === undefined ? "" : item.ISSUE_KEY,
          rec_type: item.REC_TYPE,
          row_id: item.ROW_ID
        });
      });
    }

    // Calculate total basket amount
    for (let k = 0; k < json_arrOBJ.length; k++) {
      total_basket = total_basket + parseInt(json_arrOBJ[k].basket_amount || 0);
    }

    const unsourced_amt = (this.rowData.ADJUSTED_BOOK - total_basket);

    // Validate total
    if (total_basket !== this.rowData.ADJUSTED_BOOK && this.rowData.DEFAULT_SOURCE === 'B') {
      this.gtwSnackbarService.openSnackBar("Total Sourced amount is not equal to GHOST amount.", 'gtw-snackbar--red');
      return;
    } else if (this.rowData.DEFAULT_SOURCE === 'A' && (total_basket + unsourced_amt) !== this.rowData.ADJUSTED_BOOK) {
      this.gtwSnackbarService.openSnackBar("Total Sourced amount is greater than GHOST amount", 'gtw-snackbar--red');
      return;
    } else {
      this.saveBasket(json_arrOBJ, unsourced_amt);
    }
  }

  /**
   * Save Basket Data
   * Following AngularJS saveBasket() function
   */
  saveBasket(json_arrOBJ: any[], unsourced_amt: number): void {
    // Validate flags
    if (this.issueIdEnabled() &&
        (this.basketRowFlag === true || this.issueIdRowFlag === true || this.issueRowFlag === true)) {
      return;
    }

    if (!this.issueIdEnabled() && this.basketRowFlag === true) {
      return;
    }

    this.crudLoading = true;
    this.isSaveClicked = true;
    this.cdr.detectChanges();

    const saveSeq = this.loadSeq;

    // Build settings
    const settings: any = {
      tax_year: this.filterParams?.tax_year || this.globalService?.tax_year || '',
      scenario: this.filterParams?.scenario || this.globalService?.scenario || '74',
      jcd_key: this.globalService?.jcd_key || '250',
      unsourced_amt: unsourced_amt,
      ho_combination_key: this.rowData.HO_COMBINATION_KEY
    };

    const newSettings = _.merge({}, settings, this.rowData);

    this.sourcingInputAppBasketService.saveGhostSourcingBasketData(
      this.apiBaseUrl(),
      json_arrOBJ,
      newSettings,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (saveSeq !== this.loadSeq) {
          return;
        }
        this.crudLoading = false;
        this.isSaveClicked = false;
        
        if (response.callSuccess === "1") {
          this.gtwSnackbarService.openSnackBar(this.saveSuccess, 'gtw-snackbar--green');
          if (response.unsourced_amt != null && response.unsourced_amt !== undefined) {
            this.unsourced_amt_modal = response.unsourced_amt;
            this.rowData.UNSOURCED_APPORTIONED_AMT = response.unsourced_amt;
          }
          this.refreshBasketData();
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar(response.errorMessage || this.saveError, 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        if (saveSeq !== this.loadSeq) {
          return;
        }
        this.crudLoading = false;
        this.isSaveClicked = false;
        this.gtwSnackbarService.openSnackBar(this.saveError, 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Change Baskets dropdown
   */
  changeBaskets(value1: any, value2: number): void {
    if (this.objects[value2]) {
      this.objects[value2].basketFlag = false;
    }
    this.cdr.detectChanges();
  }

  /**
   * Change Issue ID for new baskets
   */
  changeEditIssueId(value: any, value3: number): void {
    if (this.objects[value3]) {
      this.objects[value3].issueIdFlag = false;
    }
    this.cdr.detectChanges();
  }

  /**
   * Change Issue ID for existing baskets
   */
  changeIssueId(value: any, value3: number): void {
    this.basketTable[value3].issueFlag = false;
    this.cdr.detectChanges();
  }

  reset(): void {
    this.crudLoading = false;
    if (this.modalType === 'app') {
      // Reset App Modal - restore original values loaded when modal opened
      if (this.originalSelectedAppBase) {
        // Find the matching app base from current list (in case list was refreshed)
        const matchingAppBase = this.appBases.find((base: any) => 
          base.BASE_ID === this.originalSelectedAppBase.BASE_ID
        );
        this.selectedAppBase = matchingAppBase || this.originalSelectedAppBase;
      } else {
        this.selectedAppBase = null;
      }
      
      if (this.originalSelectedIssueId) {
        // Find the matching issue ID from current list (in case list was refreshed)
        const matchingIssueId = this.appIssueId.find((issue: any) => 
          issue.ISSUE_ID === this.originalSelectedIssueId.ISSUE_ID
        );
        this.selectedIssueId = matchingIssueId || this.originalSelectedIssueId;
      } else {
        this.selectedIssueId = null;
      }
      
      // Restore original BASE_LEVEL
      this.BASE_LEVEL = this.originalBaseLevel;
      
      // Clear validation flags
      this.app_issue_flag = false;
    } else {
      // Reset Basket Modal - restore original values without API calls
      // Clear new baskets
      this.objects = [];
      this.newBasketTables = [];
      this.showNewBasketTable = false;
      
      // Restore original basket table (deep copy to avoid reference issues)
      if (this.originalBasketTable && this.originalBasketTable.length > 0) {
        this.basketTable = safeJsonInput<any>(JSON.stringify(this.originalBasketTable), null, 'sourcing-input-app-basket.parse10');
        this.default_basket = this.basketTable.find((x: any) => x.REC_TYPE === 'D');
      } else {
        this.basketTable = [];
        this.default_basket = null;
      }
      
      // Restore original unsourced amount
      this.unsourced_amt_modal = this.originalUnsourcedAmt;
      
      // Clear validation flags
      this.basketRowFlag = false;
      this.issueIdRowFlag = false;
      this.issueRowFlag = false;
    }
    this.cdr.detectChanges();
  }

  /**
   * Compare function for App Base dropdown
   */
  compareAppBase(base1: any, base2: any): boolean {
    return base1 && base2 ? base1.BASE_ID === base2.BASE_ID : base1 === base2;
  }

  /**
   * Compare function for Issue ID dropdown
   */
  compareIssueId(issue1: any, issue2: any): boolean {
    return issue1 && issue2 ? issue1.ISSUE_ID === issue2.ISSUE_ID : issue1 === issue2;
  }

  cancel(): void {
    this.loadSeq++;
    this.isInitialized = false;
    this.initScheduled = false;
    this.rowDataFromHost = false;
    this.filterParamsFromHost = false;
    this.crudLoading = false;
    this.isSaveClicked = false;

    this.disableTable = false;
    this.basketTable = [];
    this.loading = true;
    this.hasServiceError = false;
    
    // CRITICAL: Use EventEmitter for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
    // NO $uibModalInstance.dismiss('cancel') or this.dialog.closeAll()
  }
}
